import type { ColorInput, TinyColor as TinyColorType } from "@ctrl/tinycolor";
import TinyColor, { Numberify, RGBA } from "@ctrl/tinycolor";
import { useTranslation } from "@redotech/redo-i18n";
import DropperIcon from "@redotech/redo-icons/dropper_filled.svg";
import {
  ChangeEvent,
  createContext,
  KeyboardEvent,
  memo,
  useContext,
  useRef,
  useState,
} from "react";
import { Alpha, Hue, Saturation } from "react-color/lib/components/common";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { RedoButton } from "../buttons/redo-button";
import * as colorPickerCSS from "./redo-color-picker.module.css";

/**
 * Browser native `EyeDropper` API (Chromium-based browsers). Not available
 * in Firefox or Safari — feature-detect before calling.
 *
 * Spec: https://developer.mozilla.org/en-US/docs/Web/API/EyeDropper_API
 */
type EyeDropperConstructor = new () => { open(): Promise<{ sRGBHex: string }> };
declare global {
  interface Window {
    EyeDropper?: EyeDropperConstructor;
  }
}

const hasEyeDropper = (): boolean =>
  typeof window !== "undefined" && typeof window.EyeDropper === "function";

export const formatColorOutput = (
  color: TinyColorType,
  showAlpha: boolean,
): string => {
  // Only use hex8 format when showAlpha is true AND transparency < FF (alpha < 1.0)
  // The practical reason is because Outlook for Windows does not support hex8 format
  // and will render it as fully transparent.
  return showAlpha && color.getAlpha() < 1.0
    ? color.toHex8String()
    : color.toHexString();
};

export interface BrandKitColor {
  label: string;
  color: string;
}

export const BrandKitColorsContext = createContext<BrandKitColor[] | undefined>(
  undefined,
);

export const RedoColorPicker = memo(function RedoColorPicker({
  value,
  onChange,
  showAlpha,
  brandKitColors: brandKitColorsProp,
  "data-dd-action-name": actionName,
}: {
  value: string;
  onChange: (val: string) => void;
  showAlpha?: boolean;
  brandKitColors?: BrandKitColor[];
  "data-dd-action-name"?: string;
}) {
  const { t } = useTranslation();
  const brandKitColorsFromContext = useContext(BrandKitColorsContext);
  const brandKitColors = brandKitColorsProp ?? brandKitColorsFromContext;
  const tinycolor = TinyColor(value);
  const hsl = tinycolor.toHsl();
  const hsv = tinycolor.toHsv();
  const rgb = tinycolor.toRgb();

  const saturationPointer = () => {
    return (
      <div
        className={colorPickerCSS.saturationPointer}
        style={{ backgroundColor: value }}
      />
    );
  };
  const hueAndAlphaPointer = () => {
    return (
      <div
        className={colorPickerCSS.hueAndAlphaPointer}
        style={{ backgroundColor: value }}
      />
    );
  };

  const handleChange = (data: ColorInput) => {
    const color = TinyColor(data);
    if (!color.isValid) {
      return;
    }
    const hex = formatColorOutput(color, !!showAlpha);
    onChange(hex);
  };

  const handleEyedropper = async () => {
    if (!window.EyeDropper) {
      return;
    }
    try {
      const result = await new window.EyeDropper().open();
      handleChange(result.sRGBHex);
    } catch {
      // User cancelled (Escape / dismissed) — no-op.
    }
  };

  return (
    <div
      className={colorPickerCSS.picker}
      data-arbiter="RedoColorPicker"
      data-dd-action-name={actionName}
    >
      <div className={colorPickerCSS.saturation}>
        <Saturation
          {...{ hsl, hsv }}
          className={colorPickerCSS.Saturation}
          color={value}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          onChange={handleChange as any} // Do not listen to the type error here, react-color lies about what it passes into this callback
          pointer={saturationPointer}
        />
      </div>
      <div className={colorPickerCSS.controls}>
        <div className={colorPickerCSS.sliders}>
          <div className={colorPickerCSS.hue}>
            <Hue
              {...{ hsl }}
              className={colorPickerCSS.Hue}
              color={value}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              onChange={handleChange as any} // Do not listen to the type error here, react-color lies about what it passes into this callback
              pointer={hueAndAlphaPointer}
            />
          </div>
          {showAlpha && (
            <div className={colorPickerCSS.alpha}>
              <Alpha
                {...{ hsl, rgb }}
                className={colorPickerCSS.Alpha}
                color={value}
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                onChange={handleChange as any} // Do not listen to the type error here, react-color lies about what it passes into this callback
                pointer={hueAndAlphaPointer}
              />
            </div>
          )}
        </div>
      </div>
      <Flex dir="column" gap="md">
        <ColorPickerInputs
          onChange={handleChange}
          onEyedropper={hasEyeDropper() ? handleEyedropper : undefined}
          rgb={rgb}
          showAlpha={showAlpha}
          value={value}
        />
        {showAlpha && (
          <RedoButton
            hierarchy={rgb.a === 0 ? "primary" : "secondary"}
            onClick={() => {
              // Toggle alpha: if already transparent, restore to fully opaque;
              // otherwise set opacity to 0. Keep current RGB in both directions.
              handleChange({
                r: rgb.r,
                g: rgb.g,
                b: rgb.b,
                a: rgb.a === 0 ? 1 : 0,
              });
            }}
            size="sm"
            text={t("Transparent", { context: "color picker" })}
          />
        )}
      </Flex>
      {brandKitColors && brandKitColors.length > 0 && (
        <Flex
          borderColor="primary"
          borderTopWidth="1px"
          dir="column"
          gap="xs"
          mt="md"
          pt="sm"
        >
          <Text fontSize="xxs" pb="xxs" textColor="secondary">
            {t("Brand colors", { context: "color picker" })}
          </Text>
          <Flex
            className={colorPickerCSS.brandKitGrid}
            dir="column"
            gap="xxs"
            overflowY="auto"
          >
            {brandKitColors.map((item) => (
              <Flex
                align="center"
                aria-label={item.label}
                borderRadius="sm"
                className={colorPickerCSS.brandKitItem}
                cursor="pointer"
                dir="row"
                gap="xs"
                key={item.label}
                onClick={() => onChange(item.color)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onChange(item.color);
                  }
                }}
                p="xxs"
                role="button"
                tabIndex={0}
              >
                <Flex
                  borderColor="primary"
                  borderRadius="xs"
                  borderWidth="1px"
                  className={colorPickerCSS.brandKitSwatch}
                  shrink={0}
                  style={{ backgroundColor: item.color }}
                />
                <Text fontSize="xxs" textColor="secondary">
                  {item.label}
                </Text>
              </Flex>
            ))}
          </Flex>
        </Flex>
      )}
    </div>
  );
});

const ColorPickerInputs = memo(function ColorPickerInputs({
  value,
  rgb,
  onChange,
  onEyedropper,
  showAlpha,
}: {
  value: string;
  rgb: Numberify<RGBA>;
  onChange: (data: ColorInput) => void;
  /** When provided, render an eyedropper trigger to the left of the HEX/RGB
   *  format dropdown. Omit when the browser doesn't support window.EyeDropper. */
  onEyedropper?: () => void;
  showAlpha?: boolean;
}) {
  const { t } = useTranslation();
  const [colorFormat, setColorFormat] = useState<"hex" | "rgb">("hex");

  const handleSingleChange = (r: number, g: number, b: number, a: number) => {
    const colorData = {
      r: Math.min(255, Math.max(0, r)),
      g: Math.min(255, Math.max(0, g)),
      b: Math.min(255, Math.max(0, b)),
      a: Math.min(100, Math.max(0, a)) / 100,
    };
    onChange(colorData);
  };

  return (
    <div className={colorPickerCSS.controls}>
      {onEyedropper && (
        <Flex align="flex-end" shrink={0}>
          <RedoButton
            aria-label={t("Pick a color from the page")}
            hierarchy="secondary"
            IconLeading={DropperIcon}
            onClick={onEyedropper}
            size="xs"
            title={t("Pick a color from the page")}
          />
        </Flex>
      )}
      <div className={colorPickerCSS.formatSelector}>
        <select
          className={colorPickerCSS.formatDropdown}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          onChange={(e) => setColorFormat(e.target.value as "hex" | "rgb")}
          value={colorFormat}
        >
          <option value="hex">HEX</option>
          <option value="rgb">RGB</option>
        </select>
      </div>

      {colorFormat === "hex" ? (
        <div className={colorPickerCSS.hexInput}>
          <HexInput onChange={onChange} showAlpha={showAlpha} value={value} />
        </div>
      ) : (
        <div className={colorPickerCSS.rgbaSection}>
          <div className={colorPickerCSS.rgba}>
            <input
              max="255"
              min="0"
              onChange={(e) =>
                handleSingleChange(
                  Number(e.target.value) || 0,
                  rgb.g,
                  rgb.b,
                  rgb.a,
                )
              }
              type="number"
              value={String(rgb.r)}
            />
          </div>
          <div className={colorPickerCSS.rgba}>
            <input
              max="255"
              min="0"
              onChange={(e) =>
                handleSingleChange(
                  rgb.r,
                  Number(e.target.value) || 0,
                  rgb.b,
                  rgb.a,
                )
              }
              type="number"
              value={String(rgb.g)}
            />
          </div>
          <div className={colorPickerCSS.rgba}>
            <input
              max="255"
              min="0"
              onChange={(e) =>
                handleSingleChange(
                  rgb.r,
                  rgb.g,
                  Number(e.target.value) || 0,
                  rgb.a,
                )
              }
              type="number"
              value={String(rgb.b)}
            />
          </div>
          {showAlpha && (
            <div className={colorPickerCSS.rgba}>
              <div className={colorPickerCSS.alphaInputContainer}>
                <input
                  max="100"
                  min="0"
                  onChange={(e) =>
                    handleSingleChange(
                      rgb.r,
                      rgb.g,
                      rgb.b,
                      Number(e.target.value) || 0,
                    )
                  }
                  type="number"
                  value={String(Math.round(rgb.a * 100))}
                />
                <span className={colorPickerCSS.percentageLabel}>%</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

// Match 6-digit RGB or 8-digit RGBA hex strings (with or without leading "#").
// Shorter forms (3/4-digit, single chars) are accepted by TinyColor as valid
// colors but are almost always a partial value the user is still typing — we
// treat them as draft until the user commits via Enter or blur.
const FULL_HEX_RE = /^#?[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/;

const formatDisplayHex = (value: string, showAlpha: boolean): string => {
  const color = TinyColor(value);
  return color.isValid ? formatColorOutput(color, showAlpha) : value;
};

const HexInput = memo(function HexInput({
  value,
  onChange,
  showAlpha,
}: {
  value: string;
  onChange: (data: ColorInput) => void;
  showAlpha?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  // While focused, show the user's in-progress draft; otherwise derive the
  // displayed value from the parent so external changes (e.g. slider drags)
  // stay in sync without an Effect:
  // https://react.dev/learn/you-might-not-need-an-effect
  const displayValue = isFocused ? draft : formatDisplayHex(value, !!showAlpha);

  const commit = (next: string): boolean => {
    const trimmed = next.trim();
    if (!FULL_HEX_RE.test(trimmed)) {
      return false;
    }
    onChange(formatColorOutput(TinyColor(trimmed), !!showAlpha));
    return true;
  };

  // Seed the draft so the field shows the current value the moment it is
  // focused, before any typing.
  const handleFocus = () => {
    setDraft(formatDisplayHex(value, !!showAlpha));
    setIsFocused(true);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    // A change event always implies the field is focused; keep isFocused in
    // sync so displayValue renders the draft.
    setIsFocused(true);
    setDraft(next);
    // commit() no-ops unless the draft is a complete, valid hex, so the swatch
    // updates the moment the user finishes typing one without coercing partial
    // input mid-edit.
    commit(next);
  };

  const handleBlur = () => {
    // An invalid draft is dropped: displayValue reverts to the parent value
    // once isFocused flips false.
    commit(draft);
    setIsFocused(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!commit(draft)) {
        setDraft(formatDisplayHex(value, !!showAlpha));
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsFocused(false);
      inputRef.current?.blur();
    }
  };

  return (
    <input
      onBlur={handleBlur}
      onChange={handleChange}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      ref={inputRef}
      spellCheck={false}
      value={displayValue}
    />
  );
});
